import "./FileAttachmentsUploaderStyles.scss";

import * as React from "react";
import { ErrorBoundary } from "../../../errors/ErrorBoundary";
import {
    IAttachmentsUploadConfigVm
} from "../../interfaces";
import {
    AttachmentRelatesTo,
    DefaultFileAttachmentsUploaderSettings,
    FileAttachmentsTableMode
} from "../../models";
import FileAttachmentsTable from "../viewer/FileAttachmentsTable";
import { Authorization } from "../../../misc/components/Authorization";
import { DragDrop } from "../../../misc/DragDrop";
import { useCurrentStudyConfigurationContext } from "../../../Core/CurrentStudyConfigurationContext";
import { useSecurityContext } from "../../../Core/SecurityContext";
import { File } from "webportalclient";
import { Bytes } from "@vitalograph/web-ui";
import { useWebPortalApiClient, useWebPortalLegacyClient } from "../../../misc/api/WebPortalTokenClient";
import { DcfRequest } from "webportalclient";

export interface FileAttachmentsUploaderProps
    extends IAttachmentsUploadConfigVm {
    title?: string;
    requiresPermissions?: string[];
    onFileAttachmentsUpdated?: (fileAttachments: any[]) => void;
    formState;
    dcfRequest?: DcfRequest;
    onStageExistingDelete?: (id: string) => void;
    stagedDeleteIds?: string[];
    permissionToRemoveFileAttachment?: boolean;
    isDcfEditPage?: boolean;
    disableEditFormFields?: boolean;
}

export enum AttachmentStatus {
    Existing = "existing",
    New = "new"
}

interface ExistingAttachment  {
    id: string; 
    fileName: string;
    fileSizeInBytes: number;
    status: AttachmentStatus.Existing;
};

interface NewAttachment  {
    file: File;
    fileName: string;
    fileSizeInBytes: number;
    status: AttachmentStatus.New;
};

type AnyAttachment = ExistingAttachment | NewAttachment;


const FileAttachmentsUploader = (props: FileAttachmentsUploaderProps) => {
    const [showExceedingFileCount, setShowExceedingFileCount] = React.useState(false);
    const [showValidationMessage, setShowValidationMessage] = React.useState(false);
    const [fileAttachments, setFileAttachments] = React.useState<AnyAttachment[]>([]);
    const [dropZoneDisabled, setDropZoneDisabled] = React.useState(false);
    const [isLoadingExisting, setIsLoadingExisting] = React.useState(false);
    const [stagedDeletes, setStagedDeletes] = React.useState<string[]>([]);
    const client = useWebPortalApiClient();
    const [defaultSettings, setDefaultSettings] = React.useState(DefaultFileAttachmentsUploaderSettings);
    const legacyClient = useWebPortalLegacyClient();

    const { study } = useCurrentStudyConfigurationContext();
    const { currentUserPermissions } = useSecurityContext();

    React.useEffect(() => {
        if (!props.formState.values.CorrectionRecords) {

            setFileAttachments([]);
            setDropZoneDisabled(false);
            setShowExceedingFileCount(false);
            setStagedDeletes([]);

        }
    }, [props.formState.values.CorrectionRecords])


    React.useEffect(() => {
        let isCancelled = false;

        const fetchExisting = async () => {
            if (!props.dcfRequest?.id) {
                setFileAttachments([]);
                setDropZoneDisabled(false);
                setStagedDeletes([]);
                return;
            }

            setIsLoadingExisting(true);
            try {
                const { value } = await client.client.getFileAttachments(undefined, `relatedEntityId eq ${props.dcfRequest?.entityId}`, undefined, "file");

                if (isCancelled) return;

                const existing: ExistingAttachment[] = (value ?? []).map((a: any) => ({
                    id: String(a.id),
                    fileName: a.file?.fileName ?? "",
                    fileSizeInBytes: a.file?.fileSizeInBytes ?? 0,
                    status: AttachmentStatus.Existing,
                }));


                const filtered = props.stagedDeleteIds?.length
                    ? existing.filter(e => !props.stagedDeleteIds!.includes(e.id))
                    : existing;

                setFileAttachments(filtered);
                setDropZoneDisabled(isMaxFileCountAchieved(existing.length));
            } catch (err) {
                console.error("Failed to load existing attachments:", err);
            } finally {
                if (!isCancelled) setIsLoadingExisting(false);
            }
        };

        fetchExisting();
        return () => {
            isCancelled = true;
        };
    }, [props.dcfRequest?.id]);


    const onDrop = (acceptedFiles: any[], rejectedFiles: any[]) => {
        if (rejectedFiles && rejectedFiles.length > 0) {
            setShowExceedingFileCount(false);
            setShowValidationMessage(true);
            return;
        }
        const currentCount = fileAttachments.length;
        const nextFileCount = currentCount + acceptedFiles.length;
        const fileCountExceeded = nextFileCount > getSettingMaxFileCount();

        setShowExceedingFileCount(fileCountExceeded);

        if (fileCountExceeded) {
            return;
        }


        const newItems: NewAttachment[] = acceptedFiles.map((f) => ({
            file: f,
            fileName: f.name,
            fileSizeInBytes: f.size,
            status: AttachmentStatus.New,
        }));

        const next = fileAttachments.concat(newItems);

        setFileAttachments(next);
        setDropZoneDisabled(isMaxFileCountAchieved(nextFileCount));
        setShowExceedingFileCount(fileCountExceeded);
        setShowValidationMessage(false);

        updateParentWithNextFiles(next);
    }

    const onError = (err: Error) => {
        tryRenderWarningAttachmentValidation();
    }

    const toDisplayAttachment = (a: AnyAttachment) => {
        const name =
            a.status === "existing"
                ? a.fileName || "" 
                : a.fileName || "";

        const sizeInBytesRaw =
            a.status === "existing"
                ? a.fileSizeInBytes || 0 
                : a.fileSizeInBytes || 0;

        return {
            fileName: name,
            fileSizeInBytes: Number(sizeInBytesRaw),
            status: a.status,
        };
    };

    const updateParentWithNextFiles = (next: AnyAttachment[]) => {
        if (props.onFileAttachmentsUpdated) {
           
            const newFiles = next
                .filter((a): a is NewAttachment => a.status === "new")
                .map((a) => a.file);
            props.onFileAttachmentsUpdated(newFiles);
        }
    };

    const getSettingTitle = () => {
        return props.title ? props.title : "File Attachments";
    }

    const getSettingMaxFileCount = () => {
        return props.maxFileCount ? props.maxFileCount : defaultSettings.maxFileCount;
    }

    const getSettingAllowedFileTypeExtensions = () => {
        return props.allowedFileTypeExtensions
            ? props.allowedFileTypeExtensions
            : defaultSettings.allowedFileTypeExtensions;
    }

    const getSettingMaxFileSize = () => {
        return props.maxFileSize ? props.maxFileSize : defaultSettings.maxFileSize;
    }

    const getAllowMultipleFiles = () => {
        return getSettingMaxFileCount() > 1;
    }
    const renderAttachFilesActionButton = () => {
        return (
            <button
                className={
                    "attach-files btn btn-xs btn-info" +
                    (isMaxFileCountAchieved() ? " disabled" : "")
                }
                type="button"
                disabled={isMaxFileCountAchieved()}
                onClick={(ev) => (ev.currentTarget.closest('.panel').getElementsByClassName('FileAttachments').item(0) as HTMLElement).click()}
            >
                <span className="fa fa-paperclip" aria-hidden="true" />
                &nbsp;Attach File
                {getAllowMultipleFiles() ? "s" : ""}
                {isMaxFileCountAchieved() && " (maximum files attached)"}
            </button>
        );
    }

    const tryRenderDropzone = () => {
        return (
            !dropZoneDisabled && (
                <div className="col-lg-12">
                    <DragDrop
                        id="FileAttachments"
                        className="FileAttachments"
                        options={{
                            disabled: dropZoneDisabled || props.disableEditFormFields,
                            onDrop: onDrop,
                            onError: onError,
                            accept: getSettingAllowedFileTypeExtensions(),
                            maxSize: getSettingMaxFileSize(),
                            multiple: getAllowMultipleFiles(),
                        }}
                    />
                </div>
            )
        );
    }

    const tryRenderWarningFileLimitExceeded = () => {
        return (
            showExceedingFileCount && (
                <div className="alert alert-danger">
                    Please select less multiple files as with the last try you have exceeded the
                    limit of max
                    {" " + getSettingMaxFileCount()} file attachments per submission.
                </div>
            )
        );
    }

    const tryRenderWarningAttachmentValidation = () => {
        return (
            <>
                {tryRenderWarningFileLimitExceeded()}

                {showValidationMessage && (
                    <div className="alert alert-danger">
                        Attachments were not added! You have selected files with unsupported file
                        type or exceeding file size. Please try again with valid file types{" "}
                        <strong>({Object.values(getSettingAllowedFileTypeExtensions()).flat().join(", ")})</strong>{" "}
                        and file size up to{" "}
                        <Bytes value={getSettingMaxFileSize()} />.
                    </div>
                )}
            </>
        );
    }

    const isMaxFileCountAchieved = (filesCount?: number) => {
        return (
            (filesCount ? filesCount : fileAttachments.length) >=
            getSettingMaxFileCount()
        );
    }

    const removeAttachment = (fileIndex: number) => {
        const item = fileAttachments[fileIndex];
        if (!item) return;

        const next = fileAttachments.slice();
        next.splice(fileIndex, 1);
        setFileAttachments(next);
        setDropZoneDisabled(isMaxFileCountAchieved(next.length));
        setShowExceedingFileCount(next.length > getSettingMaxFileCount());
        updateParentWithNextFiles(next);

        if (item.status === "existing") {
            props.onStageExistingDelete?.(item.id);
        }
    };

    return (
        <Authorization
            permissionIdsAny={props.requiresPermissions}
            studyId={study.id}
            currentPermissions={currentUserPermissions}
        >
            <div className="fileAttachmentsUploader col-lg-12">
                <ErrorBoundary>
                    {tryRenderWarningAttachmentValidation()}

                    <div className="panel panel-default">
                        <div className="panel-heading">
                            <h3 className="panel-title pull-left heading">
                                {getSettingTitle()}
                                {getAllowMultipleFiles()
                                    ? ` (${fileAttachments.length
                                    }/${getSettingMaxFileCount()})`
                                    : ""}
                            </h3>
                            <div
                                className="btn-group pull-right"
                                style={{ height: "12px" }}
                            >
                                {renderAttachFilesActionButton()}
                            </div>
                            <span className="clearfix" />
                        </div>
                        <div className="panel-body">
                            <FileAttachmentsTable
                                legacyClient={legacyClient}
                                fileAttachments={fileAttachments.map(toDisplayAttachment)}
                                mode={FileAttachmentsTableMode.ModifyList}
                                onRemoveAttachment={removeAttachment}
                                permissionToRemoveFileAttachment={props.permissionToRemoveFileAttachment}
                                isDcfEditPage={props.isDcfEditPage}
                            />
                            {tryRenderDropzone()}
                        </div>
                    </div>
                </ErrorBoundary>
            </div>
        </Authorization>
    );
}

export default FileAttachmentsUploader;
